import React, { useCallback, useRef, useState } from 'react';
import styles from './HoverInfoBox.module.scss';
import { Help } from '@mui/icons-material';

const HelpIcon = ({ color = 'gray' }) => {
    return (
        <Help
            style={{
                lineHeight: '1rem',
                color,
                height: '1rem',
                width: '1rem',
            }}
        />
    );
};

// Has to stay in sync with .infoBox in HoverInfoBox.module.scss.
const BOX_WIDTH = 300;
const VIEWPORT_MARGIN = 8;
// Rough estimate used only to decide if there's room below the trigger.
// The actual box height is determined by content; if our guess is off the
// box can still be partially clipped, but it's good enough for the typical
// 2–5 line tooltip case.
const ESTIMATED_BOX_HEIGHT = 180;

const HoverInfoBox = ({
    color = 'gray',
    hoverComponent = <HelpIcon color={color} />,
    children,
    style,
}) => {
    const containerRef = useRef(null);
    const [placement, setPlacement] = useState({
        horizontal: 'left',
        vertical: 'bottom',
    });

    const updatePlacement = useCallback(() => {
        const el = containerRef.current;
        if (!el || typeof window === 'undefined') return;

        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Default: box is anchored to the trigger's left edge and grows
        // to the right. If that would overflow the right edge of the
        // viewport, flip the anchor so the box grows to the left instead.
        const overflowsRight = rect.left + BOX_WIDTH + VIEWPORT_MARGIN > vw;
        const horizontal = overflowsRight ? 'right' : 'left';

        // Default: box appears below the trigger. If there's not enough
        // room below, show it above instead.
        const overflowsBottom =
            rect.bottom + ESTIMATED_BOX_HEIGHT + VIEWPORT_MARGIN > vh;
        const vertical = overflowsBottom ? 'top' : 'bottom';

        setPlacement((prev) =>
            prev.horizontal === horizontal && prev.vertical === vertical
                ? prev
                : { horizontal, vertical }
        );
    }, []);

    const containerClassName = [
        styles.hoverContainer,
        styles[`horiz_${placement.horizontal}`],
        styles[`vert_${placement.vertical}`],
    ].join(' ');

    return (
        <div
            ref={containerRef}
            className={containerClassName}
            onMouseEnter={updatePlacement}
            onFocus={updatePlacement}
            {...style}
        >
            <div className={styles.hoverElement}>{hoverComponent}</div>
            <div className={styles.infoBox}>{children}</div>
        </div>
    );
};

export default HoverInfoBox;
